import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { formatIDR, formatNumber, formatDateTime } from "@/lib/format";
import {
  ArrowDownRight, ArrowUpRight, AlertTriangle, TrendingUp, TrendingDown,
  Minus, Truck, FileText, Bell, Factory, Package, ChevronRight, Sparkles, ShieldAlert, Send
} from "lucide-react";
import { SkeletonHero, SkeletonKPI, SkeletonTable } from "@/components/ui-kit";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Iter v34aw — Dashboard refactored to Bento-Grid design.
 * Preserves ALL existing data, endpoints, permissions, testids, functionality.
 * Only visual layer changed: bento layout, glass-morphism, light cream + blue/emerald accents.
 */

const PRICE_FIELD_LABELS = {
  price_non_vat: "Harga Non PPN",
  price_ex_vat: "Harga Exc PPN",
  price_with_vat: "Harga + PPN",
  delivery_cost: "Biaya Kirim",
  unloading_fee: "Biaya Bongkar",
};

function PriceChangeDetail({ detail }) {
  if (!detail || typeof detail !== "object") return null;
  const entries = Object.entries(detail);
  return (
    <div className="mt-1 space-y-0.5">
      {entries.map(([field, { old: oldV, new: newV }]) => {
        const o = Number(oldV) || 0;
        const n = Number(newV) || 0;
        const diff = n - o;
        const tone = diff > 0 ? "text-emerald-700" : diff < 0 ? "text-red-700" : "text-stone-500";
        const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
        const arah = diff > 0 ? "Naik" : diff < 0 ? "Turun" : "Tetap";
        const fieldLabel = PRICE_FIELD_LABELS[field] || field;
        return (
          <div key={field} className={`flex items-center gap-1.5 text-[11px] ${tone}`} data-testid={`price-change-${field}`}>
            <Icon className="h-3 w-3" />
            <span className="font-semibold">{arah}</span>
            <span className="text-stone-600">·</span>
            <span className="text-stone-700">{fieldLabel}:</span>
            <span className="font-mono-num font-medium">{formatIDR(o)} → {formatIDR(n)}</span>
            {diff !== 0 && <span className="font-mono-num">({diff > 0 ? "+" : ""}{formatIDR(diff)})</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [stockAlerts, setStockAlerts] = useState({ count: 0, alerts: [] });
  const [consumption, setConsumption] = useState({ materials: [], totals: {} });
  // Iter v34cs — Kartu Plafon Kredit Klien di Dashboard (perm: dashboard.credit_limit)
  const [creditAlerts, setCreditAlerts] = useState(null);
  const [notifying, setNotifying] = useState("");

  const seeFinance = hasPerm("dashboard.finance") || hasPerm("dashboard.view");
  const seePO = hasPerm("dashboard.po") || hasPerm("dashboard.view");
  const seeSJ = hasPerm("dashboard.sj") || hasPerm("dashboard.view");
  const seeAlerts = hasPerm("dashboard.notifications") || hasPerm("dashboard.view");
  const seeBatching = hasPerm("batching.stock.view") || hasPerm("dashboard.view");
  const seeCreditLimit = hasPerm("dashboard.credit_limit");

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
    if (hasPerm("batching.stock.view")) {
      api.get("/batching/stock-alerts").then((r) => setStockAlerts(r.data || { count: 0, alerts: [] })).catch(() => {});
      api.get("/batching/consumption-stats", { params: { window_days: 30 } })
        .then((r) => setConsumption(r.data || { materials: [], totals: {} }))
        .catch(() => {});
    }
    if (hasPerm("dashboard.credit_limit")) {
      api.get("/credit-limits/alert-preview").then((r) => setCreditAlerts(r.data)).catch(() => {});
    }
  }, [hasPerm]);

  // Iter v34cs — kirim notifikasi plafon langsung dari kartu Dashboard.
  const notifyCredit = async (clientName) => {
    setNotifying(clientName || "ALL");
    try {
      const r = await api.post("/credit-limits/notify", null, { params: clientName ? { client_name: clientName } : {} });
      const d = r.data || {};
      if (d.skipped) { toast.error(d.skipped); return; }
      if (!d.eligible) { toast.error("Tidak ada klien yang mencapai ambang peringatan plafon"); return; }
      const lines = [];
      if (d.marketing) lines.push(d.marketing.ok ? `✓ Marketing → ${(d.marketing.recipients || []).join(", ")}` : `✗ Marketing: ${d.marketing.error}`);
      (d.clients || []).forEach((c) => lines.push(c.ok ? `✓ ${c.client_name} → ${c.to}` : `✗ ${c.client_name}: ${c.error}`));
      if (d.sent > 0) toast.success(`${d.sent} email terkirim`, { description: lines.join("\n"), duration: 9000 });
      else toast.error("Tidak ada email terkirim", { description: lines.join("\n"), duration: 9000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim notifikasi");
    } finally {
      setNotifying("");
    }
  };

  if (!data) return (
    <div className="min-h-full bg-[#f6f5f0]">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <SkeletonHero />
        <SkeletonKPI count={4} />
        <SkeletonTable rows={5} cols={4} />
      </div>
    </div>
  );

  const netPositive = (data.net_profit_month || 0) >= 0;

  return (
    <div className="min-h-full bg-[#f6f5f0]">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {/* Hero header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3" data-testid="dash-header">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 font-semibold">Workspace · Ringkasan</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-none text-stone-900">{t("dashboard")}</h1>
            <p className="text-stone-500 mt-2 text-sm">{data.month} · Pantauan real-time keuangan, PO, dan stok.</p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs">
            <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Sistem stabil
            </span>
          </div>
        </div>

        {/* Iter v34cs — Kartu Plafon Kredit Klien (tim penagihan) */}
        {seeCreditLimit && creditAlerts && creditAlerts.count > 0 && (
          <div className="rounded-2xl bg-red-50/60 border border-red-200 overflow-hidden" data-testid="dash-credit-limit">
            <div className="px-5 py-4 border-b border-red-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                  <ShieldAlert className="h-4.5 w-4.5 text-red-700" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-semibold">Kontrol Kredit</p>
                  <h2 className="font-display text-lg font-semibold text-stone-900">Klien Melebihi / Mendekati Plafon</h2>
                  <p className="text-xs text-stone-600 mt-0.5">
                    Berdasarkan tagihan yang sudah jatuh tempo. Klien "Melebihi Plafon" tidak dapat dibuatkan Surat Jalan baru.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-800 font-semibold" data-testid="credit-exceeded-count">
                  {creditAlerts.exceeded_count} melebihi
                </span>
                <span className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="credit-warning-count">
                  {creditAlerts.warning_count} mendekati
                </span>
                {creditAlerts.can_notify && (
                  <button onClick={() => notifyCredit(null)} disabled={!!notifying}
                    className="text-xs px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid="dash-credit-notify-all">
                    <Send className="h-3 w-3" />{notifying === "ALL" ? "Mengirim…" : "Kirim Notifikasi"}
                  </button>
                )}
                <Link to="/plafon-klien" className="text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:bg-white inline-flex items-center gap-1" data-testid="dash-credit-open-page">
                  Kelola Plafon <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            <div className="divide-y divide-red-100 max-h-[340px] overflow-y-auto bg-white">
              {(creditAlerts.rows || []).map((row) => {
                const exceeded = row.stage === "exceeded";
                return (
                  <div key={row.client_name} className="px-5 py-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`dash-credit-row-${row.client_name}`}>
                    <div className="md:col-span-4">
                      <p className="text-sm font-medium text-stone-900 truncate">{row.client_name}</p>
                      <span className={`inline-block mt-0.5 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded
                        ${exceeded ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                        {exceeded ? "Melebihi Plafon" : "Mendekati Plafon"}
                      </span>
                    </div>
                    <div className="md:col-span-3 text-right">
                      <p className="text-[10px] uppercase text-stone-500">Jatuh Tempo</p>
                      <p className="font-mono-num text-sm font-semibold text-red-700">{formatIDR(row.overdue_amount)}</p>
                    </div>
                    <div className="md:col-span-3 text-right">
                      <p className="text-[10px] uppercase text-stone-500">Batas Plafon</p>
                      <p className="font-mono-num text-xs text-stone-700">{formatIDR(row.credit_limit)}</p>
                    </div>
                    <div className="md:col-span-2 text-right flex items-center justify-end gap-2">
                      <span className={`font-mono-num text-sm font-bold ${exceeded ? "text-red-700" : "text-amber-700"}`}>
                        {Number(row.usage_pct || 0).toFixed(0)}%
                      </span>
                      {creditAlerts.can_notify && (
                        <button onClick={() => notifyCredit(row.client_name)} disabled={!!notifying}
                          className="text-stone-400 hover:text-stone-900 disabled:opacity-50" title="Kirim notifikasi ke marketing & klien"
                          data-testid={`dash-credit-notify-${row.client_name}`}>
                          <Send className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bento grid for KPIs */}
        {seeFinance && (
          <div className="grid grid-cols-12 gap-4" data-testid="dash-finance">
            {/* Hero KPI - Net Profit (big card) */}
            <div className={`col-span-12 md:col-span-6 rounded-3xl p-8 relative overflow-hidden text-white
              ${netPositive
                ? "bg-gradient-to-br from-stone-900 via-stone-800 to-emerald-900"
                : "bg-gradient-to-br from-stone-900 via-stone-800 to-red-900"}`}
              data-testid="kpi-net-profit">
              <div className={`absolute -right-20 -top-20 w-72 h-72 rounded-full blur-3xl ${netPositive ? "bg-emerald-500/20" : "bg-red-500/20"}`}></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[11px] uppercase tracking-widest text-stone-400">{t("net_profit")} · {t("monthly_reset")}</div>
                  <div className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${netPositive ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/20" : "text-red-300 bg-red-500/10 border-red-500/20"}`}>
                    {netPositive ? <ArrowUpRight className="w-3 h-3"/> : <ArrowDownRight className="w-3 h-3"/>}
                    {data.month}
                  </div>
                </div>
                <div className="font-display text-5xl md:text-6xl tracking-tight">{formatIDR(data.net_profit_month)}</div>
                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
                  <div>
                    <div className="text-[10px] uppercase text-stone-400 tracking-wider">Cash In · Bulan</div>
                    <div className="text-lg font-mono-num text-emerald-300 mt-1" data-testid="kpi-cash-in">{formatIDR(data.cash_in_month)}</div>
                    <div className="text-[10px] text-stone-500 font-mono-num">Total: {formatIDR(data.cash_in_total)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-stone-400 tracking-wider">Cash Out · Bulan</div>
                    <div className="text-lg font-mono-num text-red-300 mt-1" data-testid="kpi-cash-out">{formatIDR(data.cash_out_month)}</div>
                    <div className="text-[10px] text-stone-500 font-mono-num">Total: {formatIDR(data.cash_out_total)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Receivables + Payables side cards */}
            <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-4">
              <div className="col-span-2 rounded-2xl bg-white border border-stone-200/70 p-5" data-testid="kpi-receivables">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{t("receivables")} · {t("continuous")}</div>
                <div className="font-display text-4xl mt-3 text-blue-800">{formatIDR(data.receivables)}</div>
                <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-stone-500">{t("payables")}</span>
                  <span className="font-mono-num text-stone-700">{formatIDR(data.payables)}</span>
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-4">
                <ArrowUpRight className="w-4 h-4 text-emerald-700 mb-2"/>
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Cash In Total</div>
                <div className="font-mono-num text-lg mt-1 text-stone-900">{formatIDR(data.cash_in_total)}</div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-red-50 to-white border border-red-100 p-4">
                <ArrowDownRight className="w-4 h-4 text-red-700 mb-2"/>
                <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Cash Out Total</div>
                <div className="font-mono-num text-lg mt-1 text-stone-900">{formatIDR(data.cash_out_total)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Outstanding DP */}
        {seeFinance && Number(data.outstanding_dp_total || 0) > 0 && (
          <div className="rounded-2xl bg-amber-50/60 border border-amber-200 overflow-hidden" data-testid="dash-outstanding-dp">
            <div className="px-5 py-4 border-b border-amber-200 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">Rekonsiliasi</p>
                <h2 className="font-display text-lg font-semibold text-stone-900">Outstanding Uang Muka</h2>
                <p className="text-xs text-stone-600 mt-0.5">DP yang sudah dibayar client namun belum diaplikasikan ke invoice utama.</p>
              </div>
              <span className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 font-mono-num" data-testid="outstanding-dp-total">Total: {formatIDR(data.outstanding_dp_total)}</span>
            </div>
            <div className="divide-y divide-amber-100 max-h-[320px] overflow-y-auto bg-white">
              {(data.outstanding_dp_list || []).map((row) => (
                <div key={row.po_id} className="px-5 py-3 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid={`outstanding-dp-${row.po_number}`}>
                  <div className="md:col-span-2">
                    <p className="font-mono-num text-xs text-stone-500">{row.po_number}</p>
                    <p className="text-sm font-medium text-stone-900 truncate">{row.client_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase text-stone-500">DP Paid / Applied</p>
                    <p className="font-mono-num text-xs text-stone-700">{formatIDR(row.dp_paid)} / {formatIDR(row.dp_applied)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase text-stone-500">Outstanding</p>
                    <p className="font-mono-num text-sm font-semibold text-amber-700">{formatIDR(row.outstanding_dp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PO + SJ bento row */}
        {(seePO || seeSJ) && (
          <div className="grid grid-cols-12 gap-4">
            {seePO && (
              <div className={`${seeSJ ? "col-span-12 lg:col-span-8" : "col-span-12"} rounded-2xl bg-white border border-stone-200/70 overflow-hidden`} data-testid="dash-po">
                <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-700"/>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Progres</p>
                      <h2 className="font-display text-lg font-semibold text-stone-900">{t("po_progress")}</h2>
                    </div>
                  </div>
                  <span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{data.po_summary.length} PO</span>
                </div>
                <div className="divide-y divide-stone-100 max-h-[420px] overflow-y-auto">
                  {data.po_summary.length === 0 && (
                    <p className="px-5 py-12 text-center text-sm text-stone-400">Belum ada PO yang terdaftar.</p>
                  )}
                  {data.po_summary.map((po) => (
                    <div key={po.id} className="px-5 py-3.5 hover:bg-stone-50/60 transition" data-testid={`po-progress-${po.po_number}`}>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <p className="font-mono-num text-xs text-stone-500">{po.po_number}</p>
                          <p className="text-sm font-medium text-stone-900 truncate">{po.client_name}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-mono-num text-sm">{formatNumber(po.delivered_qty, 2)} / {formatNumber(po.total_qty, 2)}</p>
                          <p className="text-[10px] uppercase tracking-widest text-stone-500">{Math.min(100, Math.round(po.progress))}%</p>
                        </div>
                      </div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-400 rounded-full transition-all" style={{ width: `${Math.min(100, po.progress)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {seeSJ && (
              <div className={`${seePO ? "col-span-12 lg:col-span-4" : "col-span-12"} rounded-2xl bg-white border border-stone-200/70 overflow-hidden`} data-testid="dash-sj">
                <div className="px-5 py-4 border-b border-stone-100">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-emerald-700"/>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Update</p>
                      <h2 className="font-display text-lg font-semibold text-stone-900">{t("latest_sj")}</h2>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-stone-100 max-h-[420px] overflow-y-auto">
                  {data.latest_sj.length === 0 && (
                    <p className="px-5 py-12 text-center text-sm text-stone-400">Belum ada Surat Jalan.</p>
                  )}
                  {data.latest_sj.map((sj) => (
                    <div key={sj.id} className="px-5 py-3 flex items-start gap-3 hover:bg-stone-50/60 transition" data-testid={`sj-${sj.sj_number}`}>
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                        <Truck className="h-4 w-4"/>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] text-stone-500 font-mono-num">{sj.sj_number} · {sj.date}</p>
                        <p className="text-sm font-medium text-stone-900 truncate">{sj.client_name}</p>
                        <p className="text-[11px] text-stone-500 truncate">{sj.delivery_location || "—"} · {sj.vehicle_no || "—"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Batching stock alerts */}
        {seeBatching && stockAlerts.count > 0 && (
          <div className="rounded-2xl bg-red-50/60 border border-red-200 overflow-hidden" data-testid="dash-stock-alerts">
            <div className="px-5 py-4 border-b border-red-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <h2 className="font-display text-lg font-semibold text-red-800">Alert Stok Minim Batching</h2>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold">{stockAlerts.count}</span>
            </div>
            <div className="px-5 py-3 bg-white space-y-1.5">
              {stockAlerts.alerts.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-sm py-1" data-testid={`stock-alert-${a.id}`}>
                  <span className="font-medium text-stone-800">{a.name}</span>
                  <span className="font-mono-num text-xs text-stone-600">
                    Stok <strong className="text-red-700">{formatNumber(a.balance_kg)} kg</strong>
                    <span className="text-stone-400 mx-1">/ min</span>
                    <strong>{formatNumber(a.min_stock_kg)} kg</strong>
                    <span className="ml-2 text-red-600 font-semibold">−{formatNumber(a.short_kg)} kg</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consumption stats */}
        {seeBatching && consumption.materials.filter((m) => m.avg_daily_kg > 0).length > 0 && (
          <div className="rounded-2xl bg-white border border-stone-200/70 overflow-hidden" data-testid="dash-consumption-stats">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Factory className="h-4 w-4 text-blue-700" />
                <h2 className="font-display text-lg font-semibold text-stone-900">Stats Konsumsi Material (30 hari)</h2>
              </div>
              <span className="text-xs text-stone-500">Total: <strong>{formatNumber(consumption.totals.total_kg || 0)} kg</strong> · Avg: {formatNumber(consumption.totals.avg_daily_kg || 0)} kg/hari</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-wider text-stone-600">
                  <tr>
                    <th className="py-2 px-3 text-left">Material</th>
                    <th className="py-2 px-3 text-right">Saldo (kg)</th>
                    <th className="py-2 px-3 text-right">Avg / Hari</th>
                    <th className="py-2 px-3 text-right">7 Hari Terakhir</th>
                    <th className="py-2 px-3 text-right">Trend</th>
                    <th className="py-2 px-3 text-right">Habis Dalam</th>
                    <th className="py-2 px-3 text-right">Estimasi Habis</th>
                  </tr>
                </thead>
                <tbody>
                  {consumption.materials.filter((m) => m.avg_daily_kg > 0).map((m) => {
                    const critical = m.days_until_stockout !== null && m.days_until_stockout <= 7;
                    const warning = m.days_until_stockout !== null && m.days_until_stockout <= 30 && !critical;
                    return (
                      <tr key={m.id} className={`border-b border-stone-100 ${critical ? "bg-red-50/40" : warning ? "bg-amber-50/30" : ""}`} data-testid={`consumption-row-${m.id}`}>
                        <td className="py-2 px-3 font-medium">{m.name} {m.below_min_stock && <span className="ml-1 text-[10px] px-1 py-0.5 bg-red-100 text-red-700 rounded">⚠ MIN</span>}</td>
                        <td className="py-2 px-3 text-right font-mono-num">{formatNumber(m.balance_kg)}</td>
                        <td className="py-2 px-3 text-right font-mono-num">{formatNumber(m.avg_daily_kg)}</td>
                        <td className="py-2 px-3 text-right font-mono-num">{formatNumber(m.last7_kg)}</td>
                        <td className={`py-2 px-3 text-right font-mono-num text-xs ${m.trend_pct > 0 ? "text-red-600" : m.trend_pct < 0 ? "text-emerald-600" : "text-stone-500"}`}>
                          {m.trend_pct > 0 ? "▲" : m.trend_pct < 0 ? "▼" : "—"} {Math.abs(m.trend_pct)}%
                        </td>
                        <td className={`py-2 px-3 text-right font-mono-num font-semibold ${critical ? "text-red-700" : warning ? "text-amber-700" : "text-stone-700"}`}>
                          {m.days_until_stockout !== null ? `${m.days_until_stockout} hari` : "—"}
                        </td>
                        <td className="py-2 px-3 text-right text-xs text-stone-500 font-mono-num">{m.stockout_date || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Alerts */}
        {seeAlerts && (
          <div className="rounded-2xl bg-white border border-stone-200/70 overflow-hidden" data-testid="dash-alerts">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-amber-600" />
                <h2 className="font-display text-lg font-semibold text-stone-900">{t("alerts")}</h2>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">{data.alerts.length}</span>
            </div>
            <div className="divide-y divide-stone-100">
              {data.alerts.length === 0 && (
                <p className="px-5 py-10 text-center text-sm text-stone-400">Tidak ada notifikasi baru.</p>
              )}
              {data.alerts.map((a) => (
                <div key={a.id} className="px-5 py-3 flex items-start gap-3 hover:bg-amber-50/40 transition" data-testid={`alert-${a.id}`}>
                  <div className={`mt-1.5 h-2 w-2 rounded-full ${a.type === "low_stock" ? "bg-red-500" : "bg-amber-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900">{a.title}</p>
                    {a.type === "price_change" && <PriceChangeDetail detail={a.detail} />}
                    <p className="text-xs text-stone-500 font-mono-num mt-0.5">{formatDateTime(a.ts)}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    a.type === "low_stock" ? "bg-red-50 text-red-700 border border-red-100" : "bg-amber-50 text-amber-700 border border-amber-100"
                  }`}>
                    {a.type === "low_stock" ? t("low_stock") : "Price"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!seeFinance && !seePO && !seeSJ && !seeAlerts && (
          <div className="rounded-2xl bg-white border border-stone-200/70 p-12 text-center">
            <p className="text-stone-500">Anda tidak memiliki izin untuk melihat data di Dashboard. Hubungi administrator untuk menetapkan akses (dashboard.finance, dashboard.po, dashboard.sj, atau dashboard.notifications).</p>
          </div>
        )}
      </div>
    </div>
  );
}
